/**
 * Session helpers — local re-implementation of the
 * `@orolabs/oro-ui-toolkit` session API used by process-runner.
 *
 * Storage shape mirrors process-runner exactly (same keys, same value
 * types) so the same backend cookies + same `LoginResponse` payload
 * keep working without further translation.
 */

import { LS_KEYS, SS_KEYS } from './storage-keys'

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface SignedUserDetails {
  id?: string
  userName?: string
  email?: string
  firstName?: string
  lastName?: string
  picture?: string
  userType?: string
  appAdmin?: boolean | string
  buyerAdmin?: boolean | string
  anyAdmin?: boolean
  [k: string]: unknown
}

export interface TenantDetails {
  tenantId: string
  accountName?: string
  companyName?: string
  email?: string
  tenantExternalName?: string
  logo?: string
  logoBackgroundColor?: string
  additionalLogos?: { dark?: string; light?: string }
  additionalLogoBackgroundColor?: { dark?: string; light?: string }
  banner?: string
  sandbox?: boolean
}

export interface SsoClientConfig {
  authType: string
  clientId: string
  connectionName: string
  authUrl: string
  redirectUrl?: string
  pkce?: { codeChallenge: string; codeChallengeMethod: string }
}

// --------------------------------------------------------------------------
// Core session
// --------------------------------------------------------------------------

const isBrowser = typeof window !== 'undefined'

function ls(): Storage | null {
  return isBrowser ? window.localStorage : null
}
function ss(): Storage | null {
  return isBrowser ? window.sessionStorage : null
}

function readNumber(key: string): number {
  const raw = ls()?.getItem(key)
  if (!raw) return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

export function setSession(userName: string, expiryTime?: number): void {
  if (!ls()) return
  if (userName) ls()!.setItem(LS_KEYS.email, userName)
  if (typeof expiryTime === 'number') {
    ls()!.setItem(LS_KEYS.expireAt, String(expiryTime))
  }
}

export function tokenExpiresAt(): number {
  return readNumber(LS_KEYS.expireAt)
}

// --------------------------------------------------------------------------
// Auth tokens — fallback when Set-Cookie domain does not match the mobile
// app host (e.g. cookies scoped to app.dev.orolabs.ai on mobileapp.*).
// --------------------------------------------------------------------------

export interface AuthTokens {
  idToken?: string
  refreshToken?: string
}

export function setAuthTokens(tokens: AuthTokens): void {
  if (!ls()) return
  if (tokens.idToken) ls()!.setItem(LS_KEYS.idToken, tokens.idToken)
  if (tokens.refreshToken) ls()!.setItem(LS_KEYS.refreshToken, tokens.refreshToken)
}

export function getIdToken(): string {
  return ls()?.getItem(LS_KEYS.idToken) ?? ''
}

export function getRefreshToken(): string {
  return ls()?.getItem(LS_KEYS.refreshToken) ?? ''
}

export function clearAuthTokens(): void {
  ls()?.removeItem(LS_KEYS.idToken)
  ls()?.removeItem(LS_KEYS.refreshToken)
}

export function isValidSession(): boolean {
  return tokenExpiresAt() > Date.now()
}

// --------------------------------------------------------------------------
// User
// --------------------------------------------------------------------------

export function setUserInfo(user: SignedUserDetails): void {
  if (!ls()) return
  ls()!.setItem(LS_KEYS.userDetails, JSON.stringify(user))
  if (user.picture) ls()!.setItem(LS_KEYS.userPicture, user.picture)
}

export function getSignedUser(): SignedUserDetails {
  const raw = ls()?.getItem(LS_KEYS.userDetails)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as SignedUserDetails
  } catch {
    return {}
  }
}

/** Mirrors process-runner `isCurrentUserAdmin` (app/buyer admin flags). */
export function isCurrentUserAdmin(): boolean {
  const signed = getSignedUser()
  if (signed.anyAdmin === true) return true
  if (signed.appAdmin === true || signed.buyerAdmin === true) return true
  if (signed.appAdmin === 'true' || signed.buyerAdmin === 'true') return true
  return false
}

export function removeUserInfo(): void {
  ls()?.removeItem(LS_KEYS.userDetails)
  ls()?.removeItem(LS_KEYS.userPicture)
}

// --------------------------------------------------------------------------
// Tenant
// --------------------------------------------------------------------------

export function setTenantDetails(t: TenantDetails): void {
  if (!ls()) return
  ls()!.setItem(LS_KEYS.tenantId, t.tenantId)
  if (t.accountName) ls()!.setItem(LS_KEYS.accountName, t.accountName)
  if (t.companyName) ls()!.setItem(LS_KEYS.companyName, t.companyName)
  if (t.tenantExternalName) {
    ls()!.setItem(LS_KEYS.tenantExternalName, t.tenantExternalName)
  }
  if (t.email) ls()!.setItem(LS_KEYS.email, t.email)
  if (t.logo) ls()!.setItem(LS_KEYS.logo, t.logo)
  if (t.logoBackgroundColor) {
    ls()!.setItem(LS_KEYS.logoBackgroundColor, t.logoBackgroundColor)
  }
  if (t.additionalLogos) {
    ls()!.setItem(LS_KEYS.additionalLogos, JSON.stringify(t.additionalLogos))
  }
  if (t.additionalLogoBackgroundColor) {
    ls()!.setItem(
      LS_KEYS.additionalLogoBackgroundColor,
      JSON.stringify(t.additionalLogoBackgroundColor),
    )
  }
}

export function getTenantDetails(): TenantDetails | null {
  const tenantId = ls()?.getItem(LS_KEYS.tenantId)
  if (!tenantId) return null
  function parseThemeLogos(key: string): { dark?: string; light?: string } | undefined {
    const raw = ls()?.getItem(key) ?? ''
    if (!raw) return undefined
    try {
      const parsed = JSON.parse(raw) as { dark?: string; light?: string }
      return parsed && typeof parsed === 'object' ? parsed : undefined
    } catch {
      return undefined
    }
  }
  return {
    tenantId,
    accountName: ls()?.getItem(LS_KEYS.accountName) ?? undefined,
    companyName: ls()?.getItem(LS_KEYS.companyName) ?? undefined,
    tenantExternalName: ls()?.getItem(LS_KEYS.tenantExternalName) ?? undefined,
    email: ls()?.getItem(LS_KEYS.email) ?? undefined,
    logo: ls()?.getItem(LS_KEYS.logo) ?? undefined,
    logoBackgroundColor: ls()?.getItem(LS_KEYS.logoBackgroundColor) ?? undefined,
    additionalLogos: parseThemeLogos(LS_KEYS.additionalLogos),
    additionalLogoBackgroundColor: parseThemeLogos(LS_KEYS.additionalLogoBackgroundColor),
  }
}

export function removeTenantDetails(): void {
  const store = ls()
  if (!store) return
  ;[
    LS_KEYS.tenantId,
    LS_KEYS.accountName,
    LS_KEYS.companyName,
    LS_KEYS.tenantExternalName,
    LS_KEYS.logo,
    LS_KEYS.logoBackgroundColor,
    LS_KEYS.additionalLogos,
    LS_KEYS.additionalLogoBackgroundColor,
  ].forEach((k) => store.removeItem(k))
}

// --------------------------------------------------------------------------
// Tenant id from URL hash — process-runner uses `#tid_${tenantId}`
// --------------------------------------------------------------------------

export function getTenantIdFromHash(hash: string): string {
  const tidHash = hash
    .split('?')[0]
    ?.split('#')
    .find((hashValue) => hashValue.startsWith('tid_'))
  const tenantId = tidHash?.replace('tid_', '').split('--')[0] ?? ''
  return tenantId ? decodeURIComponent(tenantId) : ''
}

export function getConnectionIdFromHash(hash: string): string {
  const tidHash = hash
    .split('?')[0]
    ?.split('#')
    .find((hashValue) => hashValue.startsWith('tid_'))
  const [, ...connectionParts] = tidHash?.replace('tid_', '').split('--') ?? []
  const connection = connectionParts.join('--')
  return connection ? decodeURIComponent(connection) : ''
}

export function setTenantIdHash(tenantId: string): void {
  if (!isBrowser || !tenantId) return
  const newHash = `#tid_${encodeURIComponent(tenantId)}`
  if (window.location.hash !== newHash) {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}${newHash}`,
    )
  }
}

// --------------------------------------------------------------------------
// Pre-login destination
// --------------------------------------------------------------------------

export function setSigninJumperUrlPath(path: string): void {
  ls()?.setItem(LS_KEYS.signinJumperUrlPath, path)
}

export function getSigninJumperUrlPath(): string {
  return ls()?.getItem(LS_KEYS.signinJumperUrlPath) ?? ''
}

export function clearSigninJumperUrlPath(): void {
  ls()?.removeItem(LS_KEYS.signinJumperUrlPath)
}

// --------------------------------------------------------------------------
// SSO client config (cached loginSettings response)
// --------------------------------------------------------------------------

export function setSsoClientConfig(cfg: SsoClientConfig): void {
  if (!ls()) return
  ls()!.setItem(LS_KEYS.authType, cfg.authType)
  ls()!.setItem(LS_KEYS.clientId, cfg.clientId)
  ls()!.setItem(LS_KEYS.connection, cfg.connectionName)
  ls()!.setItem(LS_KEYS.authUrl, cfg.authUrl)
  if (cfg.pkce) {
    ls()!.setItem(LS_KEYS.codeChallenge, cfg.pkce.codeChallenge)
    ls()!.setItem(LS_KEYS.codeChallengeMethod, cfg.pkce.codeChallengeMethod)
  }
}

export function getCachedClientId(): string {
  return ls()?.getItem(LS_KEYS.clientId) ?? ''
}

// --------------------------------------------------------------------------
// Logout URL
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// SSO history — list of past SSO sign-ins per tenant. Drives the "Log in
// back to existing account" card on the login screen.
// --------------------------------------------------------------------------

export interface SsoHistoryEntry {
  /** Tenant id (used as the display name / re-login key). */
  tid: string
  /** Auth0 / IDP connection name (`google-oauth2`, `windowslive`, …). */
  connection?: string
  /** Cached Auth0 client id for the redirect rebuild. */
  clientId?: string
  /** Always `'indirect'` for buyer-app. SP entries are filtered out. */
  appType?: string
  authType?: string
  authUrl?: string
}

export function getSsoHistory(): SsoHistoryEntry[] {
  const raw = ls()?.getItem(LS_KEYS.ssoHistory)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as SsoHistoryEntry[]
    return Array.isArray(parsed)
      ? parsed.filter((e) => !e.appType || e.appType === 'indirect')
      : []
  } catch {
    return []
  }
}

export function pushSsoHistory(entry: SsoHistoryEntry): void {
  if (!entry.tid) return
  const list = getSsoHistory()
  const key = `${entry.tid}|${entry.connection ?? ''}`
  const next = [entry, ...list.filter((e) => `${e.tid}|${e.connection ?? ''}` !== key)].slice(0, 5)
  ls()?.setItem(LS_KEYS.ssoHistory, JSON.stringify(next))
}

// --------------------------------------------------------------------------
// Idle timeout (minutes) — set on tenant load
// --------------------------------------------------------------------------

export function setIdleTimeoutMins(mins: number): void {
  ls()?.setItem(LS_KEYS.idleTimeoutMins, String(mins))
}

export function getIdleTimeoutMins(): number {
  const n = readNumber(LS_KEYS.idleTimeoutMins)
  return n > 0 ? n : 60
}

export function setLogoutUrl(url: string | undefined): void {
  if (url) ls()?.setItem(LS_KEYS.logoutUrl, url)
}

export function getLogoutUrl(): string {
  return ls()?.getItem(LS_KEYS.logoutUrl) ?? ''
}

// --------------------------------------------------------------------------
// Refresh-token cross-tab lock (sessionStorage so it dies with the tab)
// --------------------------------------------------------------------------

export function getRefreshTokenLock(): boolean {
  return ss()?.getItem(SS_KEYS.refreshTokenLock) === '1'
}
export function setRefreshTokenLock(): void {
  ss()?.setItem(SS_KEYS.refreshTokenLock, '1')
}
export function clearRefreshTokenLock(): void {
  ss()?.removeItem(SS_KEYS.refreshTokenLock)
}

// --------------------------------------------------------------------------
// Locale — mirrors process-runner getSessionLocale (localStorage key: locale)
// --------------------------------------------------------------------------

const DEFAULT_LOCALE = 'en'

function standardizedLocale(value = ''): string {
  return value.replace(/_/g, '-').toLowerCase()
}

export function getSessionLocale(): string {
  const raw = ls()?.getItem('locale') ?? DEFAULT_LOCALE
  return standardizedLocale(raw)
}

export function setSessionLocale(locale: string): void {
  if (!ls()) return
  ls()!.setItem('locale', standardizedLocale(locale))
}

// --------------------------------------------------------------------------
// Nuke
// --------------------------------------------------------------------------

export function clearAllSessionStorage(): void {
  const store = ls()
  if (!store) return
  Object.values(LS_KEYS).forEach((k) => {
    // Keep signinJumperUrlPath so the post-login redirect still works after
    // a 401 wipe — process-runner uses the same convention. ssoHistory is
    // also preserved so the "log in back" card survives a logout.
    if (k === LS_KEYS.signinJumperUrlPath) return
    if (k === LS_KEYS.ssoHistory) return
    store.removeItem(k)
  })
  clearRefreshTokenLock()
  clearAuthTokens()
}
